import { useState } from 'react';

const mockTabConfig = {
  overview: { src: '/mock/overview.png', alt: 'Overview section of an InfluenceHub creator profile' },
  stats: { src: '/mock/stats.png', alt: 'Platform performance statistics from an InfluenceHub creator profile' },
  audience: { src: '/mock/audience.png', alt: 'Audience and demographics breakdown from an InfluenceHub creator profile' },
  highlights: { src: '/mock/highlights.png', alt: 'Brand partners and collaboration highlights from an InfluenceHub creator profile' },
};

export default function HowItWorksIndex() {
  const [activeTab, setActiveTab] = useState('overview');
  const config = mockTabConfig[activeTab] || mockTabConfig.overview;

  return (
    <section id="how-it-works" className="py-8 sm:py-12 bg-blue-100/30">
      <div className="container px-4 sm:px-6">
        <div className="flex flex-col lg:grid lg:grid-cols-[1.3fr_1fr] gap-8 sm:gap-12 md:gap-16 items-start">
          {/* Left: Mock profile preview */}
          <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl bg-gradient-to-br from-primary/5 to-accent/5 p-4 sm:p-6 md:p-8">
            <div className="bg-white/90 rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 shadow-lg max-w-3xl mx-auto">
              <div className="mb-4 sm:mb-5">
                <h3 className="text-base sm:text-lg font-display font-semibold text-dark mb-1">Preview your profile</h3>
                <p className="text-xs sm:text-sm text-gray-600 font-dm-sans">A real InfluenceHub profile, clean, public, and shareable.</p>
              </div>
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  {Object.keys(mockTabConfig).map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setActiveTab(tab)}
                      className={`px-3 py-1.5 rounded-full border text-xs sm:text-sm font-medium font-dm-sans transition-colors min-w-[48%] sm:min-w-0 capitalize ${
                        activeTab === tab
                          ? 'border-primary bg-white text-primary shadow-sm'
                          : 'border-gray-200 bg-transparent text-gray-600 hover:bg-white/70 hover:text-dark'
                      }`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
                <div className="relative aspect-[16/10] w-full max-w-lg sm:max-w-xl md:max-w-2xl mx-auto rounded-xl border border-gray-200 shadow-md bg-white overflow-hidden flex items-center justify-center p-3 sm:p-4">
                  <img src={config.src} alt={config.alt} className="w-full h-full object-contain" loading="lazy" />
                </div>
                <div className="text-center pt-1">
                  <a href="https://influencehub.net/bellabytheway" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center text-xs sm:text-sm font-medium text-primary hover:text-primary-light font-dm-sans underline-offset-2 hover:underline">
                    Open the full example profile →
                  </a>
                </div>
              </div>
            </div>
          </div>
          {/* Right: Steps (three pillars; "Why brands trust" lives in Why Us) */}
          <div className="space-y-4 sm:space-y-6 md:space-y-8">
            <div className="flex items-start space-x-3 sm:space-x-4 p-4 sm:p-5 rounded-xl sm:rounded-2xl bg-white border border-gray-100 transition-all duration-300">
              <div className="text-3xl sm:text-4xl flex-shrink-0">📋</div>
              <div>
                <h3 className="text-lg sm:text-xl font-display font-semibold text-dark mb-2">Showcase your platforms</h3>
                <p className="text-sm sm:text-base text-gray-600 font-dm-sans leading-relaxed">
                  Connect your platforms. <strong className="font-semibold text-dark">Your follower count, engagement rate, and average views update automatically.</strong>
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3 sm:space-x-4 p-4 sm:p-5 rounded-xl sm:rounded-2xl bg-white border border-gray-100 transition-all duration-300">
              <div className="text-3xl sm:text-4xl flex-shrink-0">🎯</div>
              <div>
                <h3 className="text-lg sm:text-xl font-display font-semibold text-dark mb-2">Professional, auto-updating media kit</h3>
                <p className="text-sm sm:text-base text-gray-600 font-dm-sans leading-relaxed">A single shareable profile that brands can open on any device, no PDFs or scattered links.</p>
              </div>
            </div>
            <div className="flex items-start space-x-3 sm:space-x-4 p-4 sm:p-5 rounded-xl sm:rounded-2xl bg-white border border-gray-100 transition-all duration-300">
              <div className="text-3xl sm:text-4xl flex-shrink-0">🚀</div>
              <div>
                <h3 className="text-lg sm:text-xl font-display font-semibold text-dark mb-2">One link to share</h3>
                <p className="text-sm sm:text-base text-gray-600 font-dm-sans leading-relaxed">Use it in email, DMs, social bios, or pitch decks so brands see the same brand-ready view every time.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
